import { useMediaQuery } from "@mui/material";
import { getDefaultApplicationGuideContent } from "pages/customForms/components/DefaultGuides";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  getProjectOverviews,
  createProjectOverview,
  updateProjectOverview,
  deleteProjectOverview,
} from "reduxStore/actions/projectOverviewActions";
import { checkRoles } from "utils/permissions";
import { v4 as uuidv4 } from "uuid";
import Tracking from "assets/images/Tracking.png";

export const useProjectOverview = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  // Redux selectors
  const user = useSelector((state) => state.user);
  const { isSystemAdmin } = checkRoles(user);
  const projectOverviews = useSelector((state) => state.projectOverviews);

  // Local state
  const [openEdit, setOpenEdit] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [description, setDescription] = useState("");
  const [overviewId, setOverviewId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const containerRefs = useRef([]);
  const defaultProjectOverviewContent = getDefaultApplicationGuideContent; // change if you want a different default
  const defaultImageURL = Tracking;

  // Open/close the edit dialog
  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  // Preprocess content before saving (optional, for consistency)
  const preprocessContentForSave = (content) =>
    content.replace(/<li data-list="bullet">/g, "<li>");

  // Sanitize content for rendering
  const sanitizeContent = (htmlContent) => {
    const updatedContent = DOMPurify.sanitize(htmlContent).replace(
      /(<p><br><\/p>)+/g,
      "<br>"
    );
    return { __html: updatedContent };
  };

  // Save or update a project overview
  const handleSaveOverview = async () => {
    try {
      const overviewData = {
        title: "Project Overview",
        description,
        content: contentBlocks.map((block) =>
          block.type === "text"
            ? { ...block, data: preprocessContentForSave(block.data) }
            : block
        ),
      };

      const action = overviewId
        ? updateProjectOverview({ ...overviewData, _id: overviewId })
        : createProjectOverview(overviewData);

      const result = await dispatch(action);
      if (result) {
        setOverviewId(result._id); // Update overview ID after saving
      }

      handleCloseEdit();
    } catch (error) {
      console.error("Error saving project overview:", error);
    }
  };

  // Delete a project overview
  const handleDeleteOverview = async () => {
    if (window.confirm("Are you sure you want to delete this overview?")) {
      const success = await dispatch(deleteProjectOverview(overviewId));
      if (success) {
        setOverviewId("");
        setContentBlocks(defaultProjectOverviewContent);
        alert("Project overview deleted successfully");
      } else {
        alert("Failed to delete project overview");
      }
    }
  };

  // Add a new text block
  const addTextBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "text", data: "" },
    ]);

  // Add a new image block
  const addImageBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "image", data: null },
    ]);

  // Update a specific block's data
  const updateBlockData = (index, data) => {
    if (index === -1 && Array.isArray(data)) {
      setContentBlocks(data);
    } else {
      setContentBlocks((prevBlocks) => {
        const updatedBlocks = [...prevBlocks];
        updatedBlocks[index] = { ...updatedBlocks[index], data };
        return updatedBlocks;
      });
    }
  };

  // Remove a specific block
  const removeBlock = (index) => {
    setContentBlocks((prevBlocks) => prevBlocks.filter((_, i) => i !== index));
  };

  // Image url helper
  const swapPublicToXxLarge = (url) =>
    url ? url.replace(/\/public$/, "/xxLarge") : defaultImageURL;

  // Fetch all project overviews on mount
  useEffect(() => {
    const fetchOverviews = async () => {
      setIsLoading(true);
      await dispatch(getProjectOverviews());
      setIsLoading(false);
    };
    fetchOverviews();
  }, [dispatch]);

  // Set content after fetching overviews
  useEffect(() => {
    if (!isLoading) {
      // Find by fixed title or your own logic (could be most recent, etc.)
      const projectOverview = projectOverviews.find(
        (overview) => overview.title === "Project Overview"
      );

      if (projectOverview) {
        setContentBlocks(
          (projectOverview.content || []).map((block) => ({
            ...block,
            id: block.id || uuidv4(),
          }))
        );
        setDescription(projectOverview.description);
        setOverviewId(projectOverview._id);
      } else {
        setContentBlocks(defaultProjectOverviewContent);
        setDescription("Default Project Overview");
        setOverviewId("");
      }
    }
  }, [projectOverviews, isLoading]);

  // Render text blocks inside their refs after changes
  useEffect(() => {
    contentBlocks.forEach((block, index) => {
      if (block.type === "text" && containerRefs.current[index]) {
        const blockRef = containerRefs.current[index];
        blockRef.innerHTML = block.data;
      }
    });
  }, [contentBlocks]);

  return {
    isLargeScreen,
    isSystemAdmin,
    openEdit,
    handleOpenEdit,
    handleCloseEdit,
    handleSaveOverview,
    handleDeleteOverview,
    addTextBlock,
    addImageBlock,
    updateBlockData,
    removeBlock,
    contentBlocks,
    sanitizeContent,
    isLoading,
    defaultImageURL,
    overviewId,
    swapPublicToXxLarge,
  };
};
